import { Box, Text } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { User } from 'lib-shared-types-api';
import { Fragment, memo } from 'react';

import { presenceApi } from 'apis/presence';
import { featureFlags } from 'utils/featureFlagging/featureFlags';

import type {
  PresenceGroup,
  PresenceStateT,
  UserPresence,
} from '../reducers/presence';

import styles from './presenceList.module.scss';
import { PresenceListItem } from './presenceListItem';
import { PresenceListSkeleton } from './presenceListSkeleton';

type PresenceListProps = {
  presenceByStatus: Array<PresenceGroup>;
  presence: PresenceStateT;
  profile: User;
  scroll: boolean;
};

export const PresenceList = memo<PresenceListProps>(
  ({ presenceByStatus, presence, profile, scroll }) => {
    const flags = featureFlags.get(['custom-away-status']);
    const { awayStatuses } = presenceApi.useReadAwayStatusesQuery(
      { isDeactivated: false },
      {
        skip: !flags.customAwayStatus,
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          awayStatuses: data?.data.customAwayStatuses ?? [],
        }),
      },
    );

    let presenceList = (
      <li>
        <h3 className={styles.emptyListItemHead}>
          There&apos;s no one here yet
        </h3>
        <p className={styles.emptyListItemText}>
          You can invite people by clicking on the &quot;+&quot; icon.
        </p>
      </li>
    );

    const isLoading = presence.meta.asyncStatus === 'loading';
    const hasPresence = presence.allPresence.length > 0;
    const hasFailed =
      presence.allPresence.length === 0 &&
      presence.meta.asyncStatus === 'failed';
    const isOffline = presence.meta.offline === true;

    if (isLoading) {
      presenceList = <PresenceListSkeleton />;
    } else if (hasPresence) {
      presenceList = (
        <>
          {presenceByStatus.map(
            (group: Array<UserPresence>, groupIndex: number) => {
              if (!group.length) return null;

              return (
                <Fragment key={groupIndex}>
                  <li className={styles.groupHead}>
                    <Text.Paragraph variant="3" color={colors.neutral['500']}>
                      {group[0].presence.status} ({group.length})
                    </Text.Paragraph>
                  </li>

                  <ul className={styles.list}>
                    {group.map((userPresence: UserPresence) => (
                      <PresenceListItem
                        key={userPresence.user.id}
                        ownPresence={presence.presence}
                        ownProfile={profile}
                        userPresence={userPresence}
                        groupIndex={groupIndex}
                        scroll={scroll}
                        awayStatuses={awayStatuses}
                      />
                    ))}
                  </ul>
                </Fragment>
              );
            },
          )}
        </>
      );
    } else if (hasFailed) {
      presenceList = (
        <li>
          <h3 className={styles.emptyListItemHead}>
            Presence loading failed..
          </h3>
        </li>
      );
    } else if (isOffline) {
      presenceList = (
        <li>
          <h3 className={styles.emptyListItemHead}>You are offline</h3>
        </li>
      );
    }

    return (
      <Box padding="0 200">
        <ul className={styles.list}>{presenceList}</ul>
      </Box>
    );
  },
);

// Adding a displayName manually since TS complains after wrapping the component in a React.memo
// @see https://github.com/yannickcr/eslint-plugin-react/issues/2133
PresenceList.displayName = 'PresenceList';
